import { createElement, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { useIntl } from 'gatsby-plugin-react-intl';

import Container from '../../../Core/Container';
import RichText from '../../../Core/RichText';
import Feature from '../../Cards/Card';
import Product from '../../Cards/Product';
import Blog from '../../Cards/Blog';
import Testimonial from '../../Cards/Testimonial';
import { ResponsiveContext } from '../../../Providers/Responsive';

import { Content, Wrapper, ShowMore } from './styles';

const columnReturn = string => string.split('-')[0];

const componentResolver = type => {
  switch (type) {
    case 'feature-card':
      return Feature;
    case 'product-card':
      return Product;
    case 'blog-card':
      return Blog;
    case 'testimonial-card':
      return Testimonial;
    default:
      return false;
  }
};

const header = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [BLOCKS.HEADING_2]: (_, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (_, children) => <h3>{children}</h3>,
  },
};

const body = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [BLOCKS.OL_LIST]: (_, children) => <ol>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (_, children) => <li>{children}</li>,
  },
};

const MaxShowLayout = ({
  data: {
    heading = '',
    headingAlignment,
    subheading = '',
    columnCount = 3,
    cards = null,
    elements,
    sectionContainerHeading,
    contentAlignment,
  },
  componentType,
  maxCardShown = 6,
}) => {
  if (!cards && !elements && !componentType) return false;
  const intl = useIntl();
  const items = cards || elements;
  const columns = columnCount ? columnReturn(columnCount) : 3;
  // Default max cards shown to 6 if none is passed down through props

  // * State to track mobile vs desktop views
  const { mobile } = useContext(ResponsiveContext);
  // If mobile device set to 2 cards shown, else based on maxCardShown value
  let maxShow = mobile ? 2 : maxCardShown;

  // * Func to change mobile or desktop views
  const updateResponsiveView = responsive => {
    if (responsive) {
      maxShow = 2;
      setMaxCards({
        show: maxShow,
        cardShown: items.slice(0, maxShow),
        displayButton: maxShow < items.length,
      });
    } else {
      maxShow = maxCardShown;
      setMaxCards({
        show: maxShow,
        cardShown: items.slice(0, maxShow),
        displayButton: maxShow < items.length,
      });
    }
  };

  useEffect(() => {
    updateResponsiveView(mobile);
  }, [mobile]);

  const [maxCards, setMaxCards] = useState({
    show: maxShow,
    cardShown: items.slice(0, maxShow),
    displayButton: maxShow < items.length,
  });
  const type = componentType.split(' ').join('-').toLowerCase();
  const component = componentResolver(type);
  return (
    <Container as="section">
      {(heading || sectionContainerHeading || subheading) && (
        <Content className={headingAlignment || contentAlignment}>
          {(heading || sectionContainerHeading) && (
            <div className="title">
              {heading && <RichText document={heading} options={header} />}
              {sectionContainerHeading && (
                <RichText document={sectionContainerHeading} options={header} />
              )}
            </div>
          )}
          {subheading && (
            <div>
              <RichText document={subheading} options={body} />
            </div>
          )}
        </Content>
      )}
      <Wrapper className={`${type} no-bullets`} columnCount={columns}>
        {maxCards.cardShown.map((data, index) =>
          createElement(component, { data, index, key: data.id }),
        )}
      </Wrapper>
      {maxCards.displayButton && (
        <ShowMore>
          <button
            type="button"
            onClick={() =>
              setMaxCards({
                show: maxCards.show + maxShow,
                cardShown: items.slice(0, maxCards.show + maxShow),
                displayButton: maxCards.show + maxShow < items.length,
              })
            }
          >
            <span>{intl.formatMessage({ id: 'ShowMore' })}</span>
          </button>
        </ShowMore>
      )}
    </Container>
  );
};

MaxShowLayout.propTypes = {
  data: PropTypes.shape().isRequired,
  componentType: PropTypes.string.isRequired,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  columnCount: PropTypes.number,
  cards: PropTypes.arrayOf(PropTypes.shape()),
  maxCardShown: PropTypes.number,
};

export default MaxShowLayout;
